// Client hooks for coaching, consulting purchase, support and attribution (spec/04 "Konsultasi, coaching" + "Akun").
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export interface CoachingCheck {
  eligible: boolean
  /** -1 = unlimited */
  remaining: number
  used: number
  limit: number
  credits: number
  planType: string
  source: 'subscription' | 'addon' | null
}
export interface CoachingSessionDto {
  id: string
  userId: string
  calcomBookingId: string | null
  calcomLink: string | null
  scheduledAt: string | null
  status: 'pending' | 'confirmed' | 'cancelled' | string
  createdAt: string
}
export interface CalcomLink {
  configured: boolean
  link: string | null
}

export const qkMisc = {
  coachingCheck: ['coaching', 'check'] as const,
  coachingList: ['coaching', 'list'] as const,
  calcomLink: ['coaching', 'calcom-link'] as const,
}

export const useCoachingCheck = (enabled = true) => useQuery({ queryKey: qkMisc.coachingCheck, queryFn: () => api<CoachingCheck>('/api/coaching/check'), enabled, staleTime: 15_000 })
export const useCoachingList = (enabled = true) => useQuery({ queryKey: qkMisc.coachingList, queryFn: () => api<{ sessions: CoachingSessionDto[] }>('/api/coaching/list'), enabled })
export const useCalcomLink = (enabled = true) => useQuery({ queryKey: qkMisc.calcomLink, queryFn: () => api<CalcomLink>('/api/coaching/calcom-link'), enabled, staleTime: 300_000 })

export function useCreateCoaching() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ session: CoachingSessionDto; accessSource: 'subscription' | 'addon' | null }>('/api/coaching/create', { method: 'POST', json: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qkMisc.coachingCheck })
      void qc.invalidateQueries({ queryKey: qkMisc.coachingList })
    },
  })
}
export function useConfirmCoaching() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { sessionId: string; calcomBookingId?: string; scheduledAt?: string }) => api<{ session: CoachingSessionDto }>('/api/coaching/confirm', { method: 'POST', json: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qkMisc.coachingList }),
  })
}

export interface ConsultingPurchase {
  ok: true
  leadId: string
  status: string
  contact: { email: string; mailto: string }
}
export const purchaseConsulting = (leadId: string) => api<ConsultingPurchase>('/api/consulting/purchase', { method: 'POST', json: { leadId } })

export const submitSupport = (input: { name: string; email: string; message: string }) => api<{ ok: true; id: string }>('/api/user/support', { method: 'POST', json: input })

// ---------- attribution (first-touch UTM captured by the inline script in __root.tsx)
export const ATTR_KEY = 'bystep.attr'
export interface StoredAttribution {
  source: string
  medium?: string | null
  campaign?: string | null
  referrer?: string | null
  landingPath?: string | null
}
export function readStoredAttribution(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(ATTR_KEY)
    if (!raw) return null
    const j = JSON.parse(raw) as StoredAttribution
    return j && typeof j.source === 'string' && j.source ? j : null
  } catch {
    return null
  }
}
/** Send the stored first-touch attribution once the user has an account, then clear it. Silent on failure. */
export async function sendStoredAttribution() {
  const a = readStoredAttribution()
  if (!a) return false
  try {
    await api('/api/auth/attribute-me', { method: 'POST', json: { source: a.source, medium: a.medium ?? undefined, campaign: a.campaign ?? undefined, referrer: a.referrer ?? undefined, landingPath: a.landingPath ?? undefined } })
    localStorage.removeItem(ATTR_KEY)
    return true
  } catch {
    return false
  }
}
